// NODE_MODULE
import { Moment } from 'moment';
import h from 'hyperscript';

// LIBS
import AggridUtils from '@libs/utils/Aggrid';
import Decimal from '@libs/utils/Decimal';
import NumericCellEditor from '@libs/agGrid/NumericCellEditor';
import SelectEditor from '@libs/agGrid/new/SelectEditor';
import TextareaEditor from '@libs/agGrid/TextareaEditor';

import NumericCellRenderer from '@libs/agGrid/cellRenderer/NumericCellRenderer';
import ProductCellRenderer from '@modules/Products/js/libs/ProductCellRenderer';
import QuoteIconCellRenderer from '@modules/Quotes/js/libs/cellRenderer/QuoteIconCellRenderer';
import TaxeCellRenderer from '@modules/Comptabilité/js/libs/TaxeCellRenderer';
import TextareaCellRenderer from '@js/libs/agGrid/TextareaCellRenderer';

import CalculTaxe from '@modules/Comptabilité/js/libs/CalculTaxe';

// CUSTOM_ELEMENT
import CE_AgGrid from '@libs/customElement/AgGrid';
import CE_TVA from '@libs/customElement/TVA';

// SERVICE
import S_Products from '@services/Product/ProductService';

class ContentBillCustomerTab extends HTMLElement {
	public static readonly tagName: string = 'ap-bill-customer-content-tab';

	private selectorTab: string = '';
	private idTab: string = '';
	private _isLock: boolean = false;

	private N_el: HTMLElement | null = null;
	private N_grid: CE_AgGrid | null = null;

	constructor() {
		super();
		this.idTab = ContentBillCustomerTab.tagName;
		this.selectorTab = this.dataset.tabContainer || '.tab-content';
	}

	public async connectedCallback() {
		this.innerHTML = `<ap-page-tabs-menu-item href="#${this.idTab}" icon="file-edit/line" icon-active="file-edit/fill" text="Contenus"></ap-page-tabs-menu-item>`;

		const N_item = this.querySelector('ap-page-tabs-menu-item');

		N_item?.addEventListener('open', () => {
			this.N_grid!.sizeColumnsToFit();
			this.getInfos();
		});
	}

	public set isLock(value: boolean) {
		this._isLock = value;
	}

	public setParentElement(parent: HTMLElement) {
		const N_container = parent.querySelector(this.selectorTab) as HTMLElement;
		this.N_el = document.createElement('div');

		this.N_el.classList.add('page-tabs-container', 'tab-pane');
		this.N_el.id = this.idTab;
		this.N_el.innerHTML = `
			<div class="page-tabs-title" display="desktop">
				Contenus
				<div class="page-tabs-title-right">
					<button class="btn btn-transparent d-none" type="button" data-type="fullscreen"></button>
				</div>
			</div>

			<div class="page-tabs-content">
				<ap-aggrid id="grid" mode="edit"></ap-aggrid>
			</div>
        `;

		this.N_grid = this.N_el!.querySelector<CE_AgGrid>('#grid');

		N_container.append(this.N_el);

		this.initGrid();
	}

	private initGrid() {
		this.N_grid!.setGridOptions({
			rowDragManaged: true,
			defaultColDef: {
				editable: false
			},
			getRowHeight: (params: any) => {
				if (params.node.rowPinned) {
					return 31 * params.data.value?.length || 1;
				}

				return 12 + 15 + 17 * (params.data.label || '').split('\n').length;
			},
			columnDefs: [{
				headerName: '',
				rowDrag: true,
				width: 60,
				suppressSizeToFit: true,
				suppressMovable: true,
				resizable: false,
				pinned: 'left',
				field: 'quoteID',
				cellRenderer: QuoteIconCellRenderer
			}, {
				headerName: S_Products.getInstance().columnNameReference,
				field: 'reference',
				width: 170,
				suppressSizeToFit: true,
				autoHeight: true,
				wrapText: true,
				colSpan: (params) => {
					return params.node?.rowPinned ? 8 : 1;
				},
				cellEditor: SelectEditor,
				cellEditorParams: {
					options: {
						table: 'products'
					}
				},
				cellClass: (params) => {
					return params.node.rowPinned ? ['ag-text-number'] : 'ag-select-editor';
				},
				cellRenderer: (params) => {
					if (params.node.rowPinned) {
						const N_div = document.createElement('div');
						N_div.classList.add('d-flex');
						N_div.style.lineHeight = '31px';

						const N_title = document.createElement('div');
						N_title.classList.add('font-weight-bold', 'ml-auto', 'mr-2');

						const N_price = document.createElement('div');

						const title: string[] = [];
						const price: string[] = [];

						for (const item of (params.data.value || [])) {
							title.push(item.title + ' :');
							price.push(item.text);
						}

						N_title.innerHTML = title.join('<br>');
						N_price.innerHTML = price.join('<br>');

						N_div.appendChild(N_title);
						N_div.appendChild(N_price);

						return N_div;
					} else {
						const renderer = new ProductCellRenderer();
						renderer.init(params);
						return renderer.getGui();
					}
				}
			}, {
				field: 'label',
				headerName: 'Libéllé',
				autoHeight: true,
				wrapText: true,
				editable: () => {
					return !this._isLock;
				},
				cellEditor: TextareaEditor,
				cellRenderer: TextareaCellRenderer
			}, {
				field: 'quantity',
				headerName: 'Qté',
				width: 80,
				suppressSizeToFit: true,
				cellEditor: NumericCellEditor,
				cellRenderer: NumericCellRenderer,
				cellRendererParams: {
					decimalNumber: 0,
					ignoreValueZero: true
				}
			}, {
				field: 'unit',
				headerName: 'U',
				width: 80,
				suppressSizeToFit: true
			}, {
				field: 'price',
				headerName: 'PU',
				width: 100,
				suppressSizeToFit: true,
				cellEditor: NumericCellEditor,
				cellRenderer: NumericCellRenderer,
				cellRendererParams: {
					decimalNumber: 2,
					suffix: '€',
					ignoreValueZero: true
				}
			}, {
				headerName: 'TVA',
				field: 'tva',
				width: 80,
				suppressSizeToFit: true,
				editable: true,
				cellClass: 'text-right text-monospace ag-select-editor',
				cellEditor: SelectEditor,
				cellEditorParams: {
					options: {
						table: 'taxes',
						refData: {
							type: 'sale'
						}
					}
				},
				cellRenderer: TaxeCellRenderer
			}, {
				headerName: 'Total HT',
				width: 100,
				editable: false,
				suppressSizeToFit: true,
				valueGetter: (params) => {
					if (params.data.price && params.data.quantity) {
						return Decimal.setDisplayNumber(params.data.price).times(params.data.quantity).toDecimalPlaces(2).toNumber();
					}
					return 0;
				},
				cellRenderer: NumericCellRenderer,
				cellRendererParams: {
					decimalNumber: 2,
					suffix: '€',
					ignoreValueZero: true
				}
			}, {
				field: '#',
				headerName: '',
				width: 50,
				editable: false,
				suppressSizeToFit: true,
				suppressMovable: true,
				resizable: false,
				pinned: 'right',
				cellRenderer: (params) => {
					if (params.node.rowPinned || this._isLock) {
						return '';
					} else {
						const N_delete = h('ap-button.btn-action-aggrid', { attrs: { confirmation: 'true', type: 'delete' } });

						N_delete.addEventListener('click', () => {
							params.api.updateRowData({
								remove: [params.node.data]
							});

							this.dispatchEvent(new CustomEvent('update'));
						});

						const N_div = h<HTMLElement>('div.container-action-aggrid', N_delete);

						return N_div;
					}
				}
			}],
			getContextMenuItems: (params) => {
				return [
					...AggridUtils.contextMenuAddLines(params, () => {
						return {
							tva: CE_TVA.getDefaultID()
						};
					}, this._isLock)
				];
			},
			onColumnResized: (params) => {
				if (params.finished) {
					params.api.resetRowHeights();
				}
			},
			onCellEditingStarted: (params) => {
				if (params.column.getColId() === 'label') {
					params.node.setRowHeight((params.node.rowHeight || 0) + 20);
					params.api.onRowHeightChanged();
				}
			},
			onCellEditingStopped: (params) => {
				if (['label'].includes(params.column.getColId())) {
					params.node.setDataValue(params.column.getColId(), params.newValue.trim());
				}

				if (params.column.getColId() === 'reference') {
					S_Products.getInstance().getById(params.data.reference).then((data) => {
						params.node.setDataValue('quantity', 1);
						params.node.setDataValue('label', data.name);
						params.node.setDataValue('unit', data.unit || 'U');
						params.node.setDataValue('price', data.price);

						params.api.resetRowHeights();

						params.api.refreshCells({
							force: true,
							rowNodes: [params.node]
						});

						this.dispatchEvent(new CustomEvent('update'));
					});
				}

				if (['price', 'quantity', 'label'].indexOf(params.column.getColId()) !== -1) {
					params.api?.resetRowHeights();
					params.api?.refreshCells({
						force: true,
						rowNodes: [params.node]
					});
				}

				this.dispatchEvent(new CustomEvent('update'));
			},
			rowData: []
		});
	}

	public set data(content: any) {
		this.N_grid!.value = content;
		this.N_grid!.pinnedBottomValue = [{}];

		this.getInfos();
	}

	public get data(): any {
		return this.N_grid!.value;
	}

	public async getInfos() {
		const data = this.data;

		const pinnedRows: any[] = [];

		let sumPriceTTC = new Decimal(0);
		let sumPriceHT = new Decimal(0);
		const sumPriceTVA: { [key: string]: Decimal } = {};

		for (const item of data) {
			if (item.price) {
				const taxe = new CalculTaxe(item.tva);
				await taxe.init();

				const quantity = Decimal.setDisplayNumber(item.quantity);
				const priceHT = Decimal.setDisplayNumber(item.price).times(quantity);

				const priceTVA = taxe.calculatePriceTVAByHT(priceHT);
				const priceTTC = taxe.calculatePriceTTCByHT(priceHT);

				sumPriceTVA[taxe.getID()] = sumPriceTVA[taxe.getID()] || new Decimal(0);
				sumPriceTVA[taxe.getID()] = sumPriceTVA[taxe.getID()].plus(priceTVA);

				sumPriceHT = sumPriceHT.plus(priceHT);
				sumPriceTTC = sumPriceTTC.plus(priceTTC);
			}
		}

		sumPriceTTC = sumPriceTTC.toDecimalPlaces(2);
		sumPriceHT = sumPriceHT.toDecimalPlaces(2);

		let totalTVA = new Decimal(0);

		for (const tvaKey in sumPriceTVA) {
			totalTVA = totalTVA.plus(sumPriceTVA[tvaKey]);
		}

		totalTVA = totalTVA.toDecimalPlaces(2);

		pinnedRows.push({
			title: 'Total HT',
			text: sumPriceHT.setSuffixAndHumanizeNumber('€')
		});

		pinnedRows.push({
			title: 'TVA',
			text: totalTVA.setSuffixAndHumanizeNumber('€')
		});

		pinnedRows.push({
			title: 'Total TTC',
			text: sumPriceTTC.setSuffixAndHumanizeNumber('€')
		});

		this.N_grid!.pinnedBottomValue = [{ value: pinnedRows }];

		return {
			price: sumPriceHT,
			priceTTC: sumPriceTTC,
			priceTVA: totalTVA
		};
	}

	public updateDateAvancement(oldDate: Moment, newDate: Moment) {
		this.N_grid!.forEachNode((node) => {
			const label = (node.data.label || '').replace(new RegExp(oldDate.format('DD/MM/YY'), 'gmi'), newDate.format('DD/MM/YY'));
			node.setDataValue('label', label);
		});
	}

	public static register() {
		customElements.define(ContentBillCustomerTab.tagName, ContentBillCustomerTab);
	}
}

export default ContentBillCustomerTab;
